/**
 * Browser Geolocation API wrapper + distance utilities.
 * Works in both web browsers and Capacitor webviews.
 */

let cachedLocation = null
let cacheTimestamp = 0
const CACHE_MAX_AGE = 5 * 60 * 1000 // 5 minutes
const STORAGE_KEY = 'press_last_location'
let prefetchPromise = null

// Restore last known location from localStorage on load
try {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    const parsed = JSON.parse(stored)
    if (parsed.latitude && parsed.longitude) {
      cachedLocation = { latitude: parsed.latitude, longitude: parsed.longitude }
      cacheTimestamp = parsed.timestamp || 0
    }
  }
} catch { /* ignore */ }

/**
 * Get the user's current GPS coordinates.
 * Returns immediately from localStorage cache, then refreshes in background.
 * Returns null on denial or timeout.
 */
export function getUserLocation() {
  // If we have a fresh cache, return it
  if (cachedLocation && Date.now() - cacheTimestamp < CACHE_MAX_AGE) {
    return Promise.resolve(cachedLocation)
  }

  // If we have a stale cache, return it immediately but refresh in background
  if (cachedLocation) {
    _refreshLocation()
    return Promise.resolve(cachedLocation)
  }

  if (!navigator.geolocation) return Promise.resolve(null)

  // No cache at all — wait for GPS
  return _refreshLocation()
}

function _refreshLocation() {
  if (prefetchPromise) return prefetchPromise
  if (!navigator.geolocation) return Promise.resolve(null)

  prefetchPromise = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cachedLocation = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }
        cacheTimestamp = Date.now()
        prefetchPromise = null
        // Persist to localStorage for next cold start
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...cachedLocation,
            timestamp: cacheTimestamp,
          }))
        } catch { /* ignore */ }
        resolve(cachedLocation)
      },
      () => {
        prefetchPromise = null
        resolve(null)
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: CACHE_MAX_AGE }
    )
  })

  return prefetchPromise
}

/**
 * Start fetching location in the background so it's cached
 * by the time the user reaches course search.
 * Call this once on app startup.
 */
export function prefetchLocation() {
  getUserLocation()
}

/**
 * Haversine distance between two points in miles.
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3958.8 // Earth radius in miles
  const toRad = (deg) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

/**
 * Sort an array of course results by proximity to user's location.
 * Attaches `_distanceMiles` to each result. Courses without coordinates go to the end.
 */
export function sortByProximity(results, userLat, userLng) {
  if (!results || !userLat || !userLng) return results

  return results
    .map((r) => {
      const lat = r.latitude ?? null
      const lng = r.longitude ?? null
      const _distanceMiles =
        lat != null && lng != null ? haversineDistance(userLat, userLng, lat, lng) : null
      return { ...r, _distanceMiles }
    })
    .sort((a, b) => {
      if (a._distanceMiles == null && b._distanceMiles == null) return 0
      if (a._distanceMiles == null) return 1
      if (b._distanceMiles == null) return -1
      return a._distanceMiles - b._distanceMiles
    })
}

/**
 * Format a distance in miles for display.
 */
export function formatDistance(miles) {
  if (miles == null) return null
  if (miles < 1) return '< 1 mi'
  return `${Math.round(miles)} mi`
}

/**
 * Geocode a course from its name + city/state using Nominatim (OpenStreetMap).
 * Free, no API key. Returns { latitude, longitude } or null.
 * Tries city/state first (most reliable), then course name + city/state.
 */
export async function geocodeCourse(courseName, city, state) {
  if (!city && !state) return null

  // City/state is more reliable — try it first
  const queries = []
  if (city && state) queries.push(`${city}, ${state}`)
  if (courseName && city && state) queries.push(`${courseName}, ${city}, ${state}`)

  for (const q of queries) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`,
        { headers: { 'User-Agent': 'Press2DN-GolfApp/1.0' } }
      )
      if (!res.ok) continue
      const data = await res.json()
      if (data.length > 0 && data[0].lat && data[0].lon) {
        return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) }
      }
    } catch {
      // Geocoding is best-effort
    }
  }
  return null
}
